import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Settings, 
  Hash, 
  Building2, 
  Bird, 
  PawPrint,
  Turtle,
  Save,
  RefreshCw,
  AlertTriangle,
  Info
} from 'lucide-react';
import { hasPermission } from '../lib/permissions';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';

const ANIMAL_GROUPS = [
  { value: 'AVE', label: 'Aves', icon: Bird, color: 'bg-sky-500' },
  { value: 'MAM', label: 'Mamíferos', icon: PawPrint, color: 'bg-amber-500' },
  { value: 'REPT', label: 'Répteis', icon: Turtle, color: 'bg-green-600' }
];

const EXAM_SEQUENCE_TYPES = [
  { value: 'interno', label: 'Exames Internos', prefix: 'EXI', color: 'bg-sky-600' },
  { value: 'externo', label: 'Exames Externos', prefix: 'EXE', color: 'bg-violet-600' }
];

export default function SequenceConfigPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bases, setBases] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [examConfigs, setExamConfigs] = useState([]);
  const [selectedBase, setSelectedBase] = useState('all');
  
  // Edit dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [newNumber, setNewNumber] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [basesRes, configsRes, examConfigsRes] = await Promise.all([
        axios.get(`${API}/bases`),
        axios.get(`${API}/sequence-configs`),
        axios.get(`${API}/exam-sequence-configs`)
      ]);
      setBases(basesRes.data);
      setConfigs(configsRes.data);
      setExamConfigs(examConfigsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (base, group) => {
    const existingConfig = configs.find(
      c => c.base_id === base.id && c.animal_group === group.value
    );
    
    setEditingConfig({
      base_id: base.id,
      base_name: base.name,
      animal_group: group.value,
      group_label: group.label,
      current_number: existingConfig?.next_number || 1
    });
    setNewNumber(existingConfig?.next_number?.toString() || '1');
    setShowEditDialog(true);
  };

  const handleExamEditClick = (base, type) => {
    const existingConfig = examConfigs.find(
      c => c.base_id === base.id && c.laboratory_type === type.value
    );

    setEditingConfig({
      mode: 'exam',
      base_id: base.id,
      base_name: base.name,
      laboratory_type: type.value,
      group_label: type.label,
      animal_group: existingConfig?.prefix || type.prefix,
      current_number: existingConfig?.next_number || 1
    });
    setNewNumber(existingConfig?.next_number?.toString() || '1');
    setShowEditDialog(true);
  };

  const handleSaveConfig = async () => {
    if (!editingConfig || !newNumber) return;
    
    const num = parseInt(newNumber);
    if (isNaN(num) || num < 1) {
      toast.error('O número deve ser maior que 0');
      return;
    }
    
    setSaving(true);
    try {
      if (editingConfig.mode === 'exam') {
        await axios.put(
          `${API}/exam-sequence-config/${editingConfig.base_id}/${editingConfig.laboratory_type}`,
          { next_number: num, prefix: editingConfig.animal_group }
        );
      } else {
        await axios.put(
          `${API}/sequence-config/${editingConfig.base_id}/${editingConfig.animal_group}`,
          { next_number: num }
        );
      }
      
      toast.success('Configuração salva com sucesso!');
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Apenas usuários com permissão administrativa podem alterar esta configuração');
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao salvar');
      }
    } finally {
      setSaving(false);
    }
  };

  const getConfigForBaseGroup = (baseId, group) => {
    return configs.find(c => c.base_id === baseId && c.animal_group === group);
  };

  const getExamConfigForBaseType = (baseId, type) => {
    return examConfigs.find(c => c.base_id === baseId && c.laboratory_type === type);
  };

  const filteredBases = selectedBase === 'all' 
    ? bases 
    : bases.filter(b => b.id === selectedBase);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          <p className="mt-4 text-emerald-700">Carregando configurações...</p>
        </div>
      </div>
    );
  }

  const canManageSequence = hasPermission(user, 'sequence.manage');

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-emerald-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-12 w-12 object-contain"
                />
              )}
              <div className={SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}>
                <h1 className="text-lg md:text-xl font-bold text-emerald-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Configuração de Sequência IBAMA
                </h1>
                <p className="text-xs text-emerald-500">Gerenciamento de IDs por Base e Grupo</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onClick={fetchData}
                variant="outline"
                size="sm"
                className="border-emerald-600 text-emerald-600"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="border-emerald-600 text-emerald-600"
                data-testid="back-btn"
              >
                Voltar
              </Button>
              <Button
                onClick={logout}
                variant="ghost"
                size="sm"
                className="text-red-600 hover:bg-red-50"
              >
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Info Banner */}
        {!canManageSequence && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-800 font-medium">Modo Visualização</p>
              <p className="text-sm text-amber-700">
                Apenas usuários com permissão administrativa podem alterar as configurações de sequência.
              </p>
            </div>
          </div>
        )}

        {/* Explanation Card */}
        <Card className="mb-6 border-emerald-100 bg-emerald-50/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-emerald-800 font-medium">Como funciona o ID IBAMA?</p>
                <p className="text-sm text-emerald-700 mt-1">
                  O ID IBAMA é gerado automaticamente no formato <strong>GRUPO NNNN</strong> (ex: AVE 0001, MAM 0015).
                  Cada base possui sua própria sequência para cada grupo de animais.
                  O número configurado aqui será o <strong>próximo ID</strong> a ser atribuído.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filter */}
        <Card className="mb-6 border-emerald-100">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Label className="flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Filtrar por Base
              </Label>
              <Select value={selectedBase} onValueChange={setSelectedBase}>
                <SelectTrigger className="w-64" data-testid="filter-base-select">
                  <SelectValue placeholder="Todas as bases" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as bases</SelectItem>
                  {bases.map(base => (
                    <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Configs Grid */}
        <div className="space-y-6">
          {filteredBases.map(base => (
            <Card key={base.id} className="border-emerald-100" data-testid={`base-card-${base.id}`}>
              <CardHeader className="bg-emerald-50 border-b border-emerald-100">
                <div className="flex items-center gap-3">
                  <Building2 className="w-5 h-5 text-emerald-600" />
                  <div>
                    <CardTitle className="text-emerald-800">{base.name}</CardTitle>
                    <CardDescription>{base.city}, {base.state}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {ANIMAL_GROUPS.map(group => {
                    const config = getConfigForBaseGroup(base.id, group.value);
                    const GroupIcon = group.icon;
                    const nextNumber = config?.next_number || 1;
                    const nextId = `${group.value} ${nextNumber.toString().padStart(4, '0')}`;
                    
                    return (
                      <div 
                        key={group.value}
                        className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
                        data-testid={`config-${base.id}-${group.value}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge className={`${group.color} text-white`}>
                              <GroupIcon className="w-3 h-3 mr-1" />
                              {group.label}
                            </Badge>
                          </div>
                          {canManageSequence && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditClick(base, group)}
                              className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                              data-testid={`edit-btn-${base.id}-${group.value}`}
                            >
                              <Settings className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Próximo número:</span>
                            <span className="font-bold text-lg text-emerald-700">{nextNumber}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Próximo ID:</span>
                            <Badge variant="outline" className="font-mono text-emerald-800 border-emerald-300">
                              <Hash className="w-3 h-3 mr-1" />
                              {nextId}
                            </Badge>
                          </div>
                          {config?.updated_at && (
                            <div className="text-xs text-gray-400 pt-2 border-t">
                              Atualizado: {new Date(config.updated_at).toLocaleDateString('pt-BR')}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 border-t pt-6">
                  <h3 className="mb-3 font-semibold text-emerald-800">Sequência de solicitações de exames</h3>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {EXAM_SEQUENCE_TYPES.map(type => {
                      const config = getExamConfigForBaseType(base.id, type.value);
                      const nextNumber = config?.next_number || 1;
                      const prefix = config?.prefix || type.prefix;
                      const nextId = `${prefix}-${nextNumber.toString().padStart(4, '0')}`;

                      return (
                        <div key={type.value} className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between mb-3">
                            <Badge className={`${type.color} text-white`}>{type.label}</Badge>
                            {canManageSequence && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleExamEditClick(base, type)}
                                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                              >
                                <Settings className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-500">Próximo número:</span>
                              <span className="font-bold text-lg text-emerald-700">{nextNumber}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-500">Próxima solicitação:</span>
                              <Badge variant="outline" className="font-mono text-emerald-800 border-emerald-300">
                                <Hash className="w-3 h-3 mr-1" />
                                {nextId}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredBases.length === 0 && (
          <Card className="border-emerald-100">
            <CardContent className="py-12 text-center">
              <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nenhuma base encontrada</p>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-800">
              <Hash className="w-5 h-5" />
              Editar Sequência
            </DialogTitle>
            <DialogDescription>
              Configure o próximo número da sequência
            </DialogDescription>
          </DialogHeader>
          
          {editingConfig && (
            <div className="space-y-4">
              <div className="p-3 bg-emerald-50 rounded-lg">
                <p className="font-medium text-emerald-800">{editingConfig.base_name}</p>
                <p className="text-sm text-gray-600">{editingConfig.group_label}</p>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Número atual:</p>
                <p className="text-xl font-bold text-gray-700">{editingConfig.current_number}</p>
                <p className="text-xs text-gray-400 mt-1">
                  Código atual: {editingConfig.mode === 'exam'
                    ? `${editingConfig.animal_group}-${editingConfig.current_number.toString().padStart(4, '0')}`
                    : `${editingConfig.animal_group} ${editingConfig.current_number.toString().padStart(4, '0')}`}
                </p>
              </div>
              
              <div>
                <Label htmlFor="newNumber">Novo número inicial</Label>
                <Input
                  id="newNumber"
                  type="number"
                  min="1"
                  value={newNumber}
                  onChange={(e) => setNewNumber(e.target.value)}
                  className="mt-2"
                  data-testid="new-number-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Novo código: {editingConfig.mode === 'exam'
                    ? `${editingConfig.animal_group}-${(parseInt(newNumber) || 1).toString().padStart(4, '0')}`
                    : `${editingConfig.animal_group} ${(parseInt(newNumber) || 1).toString().padStart(4, '0')}`}
                </p>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    <strong>Atenção:</strong> Alterar para um número menor que o atual pode gerar IDs duplicados.
                    Use esta função apenas para corrigir erros ou iniciar uma nova série.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowEditDialog(false)}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveConfig}
                  disabled={saving}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="save-config-btn"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Salvar
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
